/**
 * @file offline/offlineStore.js
 * @description Data access layer for offline storage. All reads/writes go through
 * this module. Saves to IndexedDB and queues sync operations.
 */
import db from "./offlineDb";

/** Generate a client-side UUID */
function generateId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const offlineStore = {
  // ==================== INSPECTIONS ====================

  async saveInspection(data) {
    const now = new Date().toISOString();
    const existing = data.id ? await db.inspections.get(data.id) : null;
    const record = {
      ...data,
      id: data.id || generateId(),
      _serverId: data._serverId || data.id || null,
      _syncStatus: data._syncStatus || "pending",
      _lastModified: now,
    };
    await db.inspections.put(record);
    if (record._syncStatus === "pending") {
      await db.syncQueue.add({
        table: "inspections",
        recordId: record.id,
        operation: existing ? "update" : "create",
        createdAt: now,
        status: "pending",
        data: record,
      });
    }
    return record;
  },

  async getInspection(id) {
    return db.inspections.get(id);
  },

  async getAllInspections() {
    return db.inspections.toArray();
  },

  // ==================== RESPONSES ====================

  async saveResponse(inspectionId, responseData) {
    const now = new Date().toISOString();
    const record = {
      ...responseData,
      id: responseData.id || generateId(),
      inspectionId,
      _serverId: responseData._serverId || responseData.id || null,
      _syncStatus: "pending",
      _lastModified: now,
    };
    await db.responses.put(record);
    return record;
  },

  async saveResponses(inspectionId, responsesArray) {
    const now = new Date().toISOString();
    const records = responsesArray.map((r) => ({
      ...r,
      id: r.id || generateId(),
      inspectionId,
      _serverId: r._serverId || r.id || null,
      _syncStatus: "pending",
      _lastModified: now,
    }));
    await db.responses.bulkPut(records);
    await db.syncQueue.add({
      table: "responses",
      recordId: inspectionId,
      operation: "bulk_update",
      createdAt: now,
      status: "pending",
      data: { inspectionId, responses: records },
    });
    return records;
  },

  async getResponses(inspectionId) {
    return db.responses.where("inspectionId").equals(inspectionId).toArray();
  },

  // ==================== PHOTOS ====================

  async savePhoto(inspectionId, responseId, photoData) {
    const now = new Date().toISOString();
    const id = generateId();
    const record = {
      id,
      inspectionId,
      responseId: responseId || null,
      _serverId: null,
      _syncStatus: "pending",
      _lastModified: now,
      blob: photoData.blob,
      thumbnail: photoData.thumbnail || null,
      filename: photoData.filename || "photo.jpg",
      mimeType: photoData.mimeType || "image/jpeg",
      size: photoData.size || 0,
      caption: photoData.caption || "",
      annotationData: null,
      localUrl: null,
    };
    await db.photos.put(record);
    await db.syncQueue.add({
      table: "photos",
      recordId: id,
      operation: "upload",
      createdAt: now,
      status: "pending",
      data: { id, inspectionId, responseId },
    });
    return record;
  },

  async getPhotos(inspectionId) {
    return db.photos.where("inspectionId").equals(inspectionId).toArray();
  },

  async getPhotosForResponse(responseId) {
    return db.photos.where("responseId").equals(responseId).toArray();
  },

  async deletePhoto(photoId) {
    const photo = await db.photos.get(photoId);
    if (photo && photo._serverId) {
      await db.syncQueue.add({
        table: "photos",
        recordId: photoId,
        operation: "delete",
        createdAt: new Date().toISOString(),
        status: "pending",
        data: { serverId: photo._serverId, inspectionId: photo.inspectionId },
      });
    }
    await db.photos.delete(photoId);
  },

  // ==================== TEMPLATES ====================

  async cacheTemplate(template, sections) {
    const now = new Date().toISOString();
    await db.templates.put({ ...template, _lastModified: now });
    if (sections && sections.length > 0) {
      for (const section of sections) {
        const { items, ...sectionData } = section;
        await db.templateSections.put(sectionData);
        if (items && items.length > 0) {
          await db.templateItems.bulkPut(items);
        }
      }
    }
  },

  async getTemplates() {
    return db.templates.toArray();
  },

  async getTemplate(templateId) {
    const template = await db.templates.get(templateId);
    if (!template) return null;
    const sections = await db.templateSections
      .where("templateId").equals(templateId).sortBy("sortOrder");
    const sectionsWithItems = await Promise.all(
      sections.map(async (section) => {
        const items = await db.templateItems
          .where("sectionId").equals(section.id).sortBy("sortOrder");
        return { ...section, items };
      })
    );
    return { template, sections: sectionsWithItems };
  },

  // ==================== DASHBOARD ====================

  async cacheDashboard(data) {
    await db.dashboardCache.put({
      key: "dashboard",
      data,
      _lastModified: new Date().toISOString(),
    });
  },

  async getCachedDashboard() {
    return db.dashboardCache.get("dashboard");
  },

  // ==================== SYNC STATUS ====================

  async getPendingCount() {
    return db.syncQueue.where("status").equals("pending").count();
  },

  async getPendingOperations() {
    return db.syncQueue.where("status").equals("pending").sortBy("createdAt");
  },

  async markSynced(queueId) {
    await db.syncQueue.update(queueId, { status: "completed" });
  },

  async markFailed(queueId, error) {
    await db.syncQueue.update(queueId, {
      status: "failed",
      error: error || "Unknown error",
      lastAttempt: new Date().toISOString(),
    });
  },

  async markRecordSynced(table, id, serverId) {
    const tableRef = db[table];
    if (tableRef) {
      await tableRef.update(id, { _syncStatus: "synced", _serverId: serverId || id });
    }
  },

  async cleanupSyncQueue() {
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    await db.syncQueue
      .where("status").equals("completed")
      .filter((entry) => entry.createdAt < cutoff)
      .delete();
  },

  async logConflict(table, recordId, localData, serverData) {
    await db.conflictLog.add({
      table, recordId, localData, serverData,
      createdAt: new Date().toISOString(),
    });
  },

  async cacheFullInspection(inspectionData, responses, photos) {
    const now = new Date().toISOString();
    await db.inspections.put({
      ...inspectionData,
      _serverId: inspectionData.id,
      _syncStatus: "synced",
      _lastModified: now,
    });
    if (responses && responses.length > 0) {
      const responseRecords = responses.map((r) => ({
        ...r,
        inspectionId: inspectionData.id,
        _serverId: r.id,
        _syncStatus: "synced",
        _lastModified: now,
      }));
      await db.responses.bulkPut(responseRecords);
    }
    if (photos && photos.length > 0) {
      const photoRecords = photos.map((p) => ({
        ...p,
        inspectionId: inspectionData.id,
        _serverId: p.id,
        _syncStatus: "synced",
        _lastModified: now,
      }));
      await db.photos.bulkPut(photoRecords);
    }
    return { needsTemplate: false };
  },

  async isInspectionCached(inspectionId) {
    const inspection = await db.inspections.get(inspectionId);
    if (!inspection) return false;
    const count = await db.responses.where("inspectionId").equals(inspectionId).count();
    return count > 0;
  },

  // ==================== CUSTOMER BUTLER (CRM) ====================
  // Generic helpers for the CRM tables (customers/properties/contacts/agents/
  // tasks). Same mechanics as inspections: write locally + queue for sync.

  /** Cache a server list into a CRM table (marks them synced; never clobbers
   *  locally-pending rows, which carry different client-generated ids). */
  async cacheCrmList(table, rows) {
    if (!db[table] || !Array.isArray(rows)) return;
    const now = new Date().toISOString();
    const records = rows.map((r) => ({
      ...r, _serverId: r.id, _syncStatus: "synced", _lastModified: now,
    }));
    await db[table].bulkPut(records);
  },

  /** Save a CRM record locally and queue it for sync. Decides create vs update
   *  from whether a synced version already exists. */
  async saveCrmRecord(table, data) {
    if (!db[table]) throw new Error("Unknown CRM table: " + table);
    const now = new Date().toISOString();
    const existing = data.id ? await db[table].get(data.id) : null;
    const operation = existing && existing._serverId ? "update" : "create";
    const record = {
      ...data,
      id: data.id || generateId(),
      _serverId: data._serverId || (existing && existing._serverId) || null,
      _syncStatus: "pending",
      _lastModified: now,
    };
    await db[table].put(record);
    await db.syncQueue.add({
      table, recordId: record.id, operation,
      createdAt: now, status: "pending", data: record,
    });
    return record;
  },

  /** All rows in a CRM table (synced cache + locally-pending), newest-ish first. */
  async getCrmAll(table) {
    if (!db[table]) return [];
    return db[table].toArray();
  },

  /** CRM rows filtered by a customerId index. */
  async getCrmByCustomer(table, customerId) {
    if (!db[table]) return [];
    return db[table].where("customerId").equals(customerId).toArray();
  },

  async clearAll() {
    await Promise.all([
      db.inspections.clear(), db.responses.clear(), db.photos.clear(),
      db.templates.clear(), db.templateSections.clear(), db.templateItems.clear(),
      db.syncQueue.clear(), db.conflictLog.clear(), db.dashboardCache.clear(),
    ]);
  },
};

export default offlineStore;
